// src/components/admin/AdminCamps.js
import React, { useState, useEffect } from 'react';
import {
  getAllUsers,
  unlockCampForUser,
  getCampCompletionSummary
} from '../../services/firebase/firestoreService';
import { db } from '../../services/firebase/config';
import { doc, updateDoc } from 'firebase/firestore';

export default function AdminCamps() {
  const [section, setSection] = useState('unlock');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const [selectedCamp, setSelectedCamp] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [actioning, setActioning] = useState(null);
  const [unlockSortKey, setUnlockSortKey] = useState('name');

  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [filterCamp, setFilterCamp] = useState('all');
  const [filterVillage, setFilterVillage] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getAllUsers();
      setUsers(data.filter(u => u.role !== 'admin'));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await getCampCompletionSummary();
      setSummary(data);
    } catch (e) {
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (section === 'summary') loadSummary();
  }, [section]);

  const showMsg = (text, type = 'success') => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const getFullName = (user) => {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
  };

  const isUnlocked = (user) => {
    return (user.unlockedCamps || []).includes(selectedCamp) ||
      (user.attendedCamps || []).includes(selectedCamp);
  };

  const handleUnlock = async (user) => {
    if (!window.confirm(`Unlock Camp ${selectedCamp} for ${getFullName(user)}?`)) return;
    setActioning(user.id);
    try {
      await unlockCampForUser(user.id, selectedCamp);
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, unlockedCamps: [...(u.unlockedCamps || []), selectedCamp] }
          : u
      ));
      showMsg(`Camp ${selectedCamp} unlocked for ${getFullName(user)}`);
    } catch (e) {
      showMsg('Error unlocking camp', 'error');
    } finally {
      setActioning(null);
    }
  };

  const handleLock = async (user) => {
    if (!window.confirm(`Lock Camp ${selectedCamp} for ${getFullName(user)}?`)) return;
    if (!window.confirm(`WARNING: This will completely reset all Camp ${selectedCamp} records for ${getFullName(user)}. This cannot be undone. Are you absolutely sure?`)) return;
    setActioning(user.id);
    try {
      const newUnlocked = (user.unlockedCamps || []).filter(c => c !== selectedCamp);
      const newAttended = (user.attendedCamps || []).filter(c => c !== selectedCamp);
      await updateDoc(doc(db, 'users', user.id), {
        unlockedCamps: newUnlocked,
        attendedCamps: newAttended
      });
      setUsers(prev => prev.map(u =>
        u.id === user.id
          ? { ...u, unlockedCamps: newUnlocked, attendedCamps: newAttended }
          : u
      ));
      showMsg(`Camp ${selectedCamp} locked and reset for ${getFullName(user)}`);
    } catch (e) {
      showMsg('Error locking camp', 'error');
    } finally {
      setActioning(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const q = searchQuery.toLowerCase();
    return (
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.village?.toLowerCase().includes(q) ||
      u.phone?.includes(q)
    );
  });

  // Unlocked (or attended) users always come first as a group; within each
  // group, sort alphabetically by whichever key is currently selected.
  const getSortedUnlockList = () => {
    const sortValue = (u) => unlockSortKey === 'village'
      ? (u.village || '').toLowerCase()
      : getFullName(u).toLowerCase();

    return [...filteredUsers].sort((a, b) => {
      const aUnlocked = isUnlocked(a);
      const bUnlocked = isUnlocked(b);
      if (aUnlocked !== bUnlocked) return aUnlocked ? 1 : -1;
      return sortValue(a).localeCompare(sortValue(b));
    });
  };

  const villages = [...new Set(users.map(u => u.village).filter(Boolean))].sort();

  const getSortedSummary = () => {
    let filtered = [...summary];
    if (filterVillage !== 'all') filtered = filtered.filter(u => u.village === filterVillage);
    if (filterCamp !== 'all') {
      const campKey = `camp${filterCamp}`;
      filtered = filtered.filter(u => u[campKey] === 'Yes');
    }
    return filtered.sort((a, b) => {
      let av = a[sortConfig.key] ?? '';
      let bv = b[sortConfig.key] ?? '';
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (key) => sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div>
      <div className="admin-section-title">Camps</div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { id: 'unlock', label: 'Unlock Camps' },
          { id: 'summary', label: 'Completion Summary' },
        ].map(s => (
          <button key={s.id}
            className={`admin-btn ${section === s.id ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
            onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>

      {statusMsg && (
        <div className={`admin-status-msg ${statusMsg.type}`} style={{ marginBottom: '16px' }}>
          {statusMsg.text}
        </div>
      )}

      {section === 'unlock' && (
        <div className="admin-card">
          <h3>Select Camp</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button key={n}
                onClick={() => setSelectedCamp(n)}
                style={{
                  padding: '12px 24px', fontSize: '16px', fontWeight: 700,
                  border: 'none', borderRadius: '10px', cursor: 'pointer',
                  background: selectedCamp === n ? '#2563eb' : '#f0f0f0',
                  color: selectedCamp === n ? '#fff' : '#333',
                  minWidth: '80px'
                }}>
                Camp {n}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Search by name, village or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px', border: '1px solid #ddd',
              borderRadius: '8px', fontSize: '15px', marginBottom: '12px',
              boxSizing: 'border-box'
            }}
          />

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <span style={{ fontSize: '13px', color: '#888', alignSelf: 'center' }}>Sort by:</span>
            <button
              onClick={() => setUnlockSortKey('name')}
              className={`admin-btn ${unlockSortKey === 'name' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '13px' }}>
              Name
            </button>
            <button
              onClick={() => setUnlockSortKey('village')}
              className={`admin-btn ${unlockSortKey === 'village' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '13px' }}>
              Village
            </button>
          </div>

          {loading ? <p>लोड होत आहे...</p> : (
            <div>
              {filteredUsers.length === 0 && (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No users found</p>
              )}
              {getSortedUnlockList().map(user => {
                const unlocked = isUnlocked(user);
                const attended = (user.attendedCamps || []).includes(selectedCamp);
                return (
                  <div key={user.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 16px', marginBottom: '8px',
                    background: unlocked ? '#f0fff4' : '#fafafa',
                    borderRadius: '10px',
                    border: `1px solid ${unlocked ? '#28a745' : '#e9ecef'}`
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>{getFullName(user)}</div>
                      <div style={{ fontSize: '13px', color: '#888' }}>
                        {user.village} · {user.phone}
                      </div>
                    </div>
                    {attended && (
                      <span style={{ fontSize: '12px', color: '#28a745', fontWeight: 600 }}>
                        ✓ Attended
                      </span>
                    )}
                    {unlocked && !attended && (
                      <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>
                        ✓ Unlocked
                      </span>
                    )}
                    {unlocked ? (
                      <button
                        className="admin-btn admin-btn-danger"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                        disabled={actioning === user.id}
                        onClick={() => handleLock(user)}>
                        {actioning === user.id ? '...' : 'Lock'}
                      </button>
                    ) : (
                      <button
                        className="admin-btn admin-btn-primary"
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                        disabled={actioning === user.id}
                        onClick={() => handleUnlock(user)}>
                        {actioning === user.id ? '...' : 'Unlock'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {section === 'summary' && (
        <div className="admin-card">
          <h3>Camp Completion Summary</h3>
          <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div className="admin-form-group">
              <label>Filter by Camp</label>
              <select value={filterCamp} onChange={e => setFilterCamp(e.target.value)}>
                <option value="all">All Camps</option>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>Camp {n}</option>)}
              </select>
            </div>
            <div className="admin-form-group">
              <label>Filter by Village</label>
              <select value={filterVillage} onChange={e => setFilterVillage(e.target.value)}>
                <option value="all">All Villages</option>
                {villages.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {summaryLoading ? <p>लोड होत आहे...</p> : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Name{sortArrow('name')}</th>
                    <th onClick={() => handleSort('village')} style={{ cursor: 'pointer' }}>Village{sortArrow('village')}</th>
                    <th style={{ textAlign: 'center' }}>Camp 1</th>
                    <th style={{ textAlign: 'center' }}>Camp 2</th>
                    <th style={{ textAlign: 'center' }}>Camp 3</th>
                    <th style={{ textAlign: 'center' }}>Camp 4</th>
                    <th style={{ textAlign: 'center' }}>Camp 5</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedSummary().length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        No data found
                      </td>
                    </tr>
                  ) : (
                    getSortedSummary().map(user => (
                      <tr key={user.userId}>
                        <td style={{ fontWeight: 500 }}>{user.name}</td>
                        <td>{user.village}</td>
                        {[1,2,3,4,5].map(n => (
                          <td key={n} style={{ textAlign: 'center' }}>
                            {user[`camp${n}`] === 'Yes' ? (
                              <span style={{ color: '#28a745', fontWeight: 700 }}>✓</span>
                            ) : (
                              <span style={{ color: '#ddd' }}>—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}